import { BaseApiClient } from '../base-client'
import {
  API_ROUTES,
  ApiResponse,
  ApiKeyCredentialIntegrationType,
  interpolateRoute,
  type BuildiumTaskCategoriesResponse,
  type BuildiumStaffUsersResponse,
  type BuildiumVendorsResponse,
} from '@super/shared-types'
import { withErrorHandling } from '../../utils'

export interface BuildiumMetadata {
  // Work Order defaults
  defaultAssignedToUserId: string
  defaultVendorId: string
  // Resident Request defaults
  defaultResidentAssigneeId?: string
  // Rental Owner Request defaults
  defaultRentalOwnerAssigneeId?: string
  // Contact Request defaults
  defaultContactAssigneeId?: string
}

/**
 * Buildium Integration API Client
 * Handles all Buildium-specific API operations
 */
export class BuildiumClient extends BaseApiClient {
  /**
   * Get install URL for Buildium (initiates OAuth flow).
   * @param returnPath - optional same-origin path (e.g. `/onboarding/integrations`)
   *   the backend should redirect to after the OAuth callback finishes. If
   *   omitted, the callback falls back to `/settings/integrations`.
   */
  getInstallUrl(returnPath?: string): string {
    const base = `${this.baseUrl}${API_ROUTES.INTEGRATIONS.BUILDIUM.INSTALL}`
    return returnPath
      ? `${base}?return=${encodeURIComponent(returnPath)}`
      : base
  }

  /**
   * Update Buildium metadata
   * Uses the generic API key credentials update endpoint with metadata only
   */
  async updateMetadata(metadata: BuildiumMetadata): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.INTEGRATIONS.API_KEY_CREDENTIALS.UPDATE,
      { integrationType: ApiKeyCredentialIntegrationType.BUILDIUM },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'PUT',
          body: JSON.stringify({ metadata }),
        })
      },
      {
        errorContext: 'Error updating Buildium metadata',
      },
    )
  }

  /**
   * [ADMIN] Update Buildium metadata for a specific company
   * Uses the generic admin API key credentials update endpoint with metadata only
   */
  async updateAdminMetadata(
    companyId: string,
    metadata: BuildiumMetadata,
  ): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.ADMIN.INTEGRATIONS.API_KEY_CREDENTIALS.UPDATE,
      { integrationType: ApiKeyCredentialIntegrationType.BUILDIUM },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'PUT',
            body: JSON.stringify({ metadata }),
          },
        )
      },
      {
        errorContext: 'Error updating admin Buildium metadata',
      },
    )
  }

  /**
   * Get task categories from Buildium
   * Used to populate searchable category dropdown in handoff form
   */
  async getTaskCategories(): Promise<BuildiumTaskCategoriesResponse> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<BuildiumTaskCategoriesResponse>
        >(API_ROUTES.INTEGRATIONS.BUILDIUM.TASK_CATEGORIES)
        return response.data || []
      },
      {
        errorContext: 'Error fetching Buildium task categories',
      },
    )
    return result || []
  }

  /**
   * [ADMIN] Get task categories from Buildium for a specific company
   */
  async getAdminTaskCategories(
    companyId: string,
  ): Promise<BuildiumTaskCategoriesResponse> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<BuildiumTaskCategoriesResponse>
        >(
          `${API_ROUTES.ADMIN.INTEGRATIONS.BUILDIUM.TASK_CATEGORIES}?companyId=${companyId}`,
        )
        return response.data || []
      },
      {
        errorContext: 'Error fetching admin Buildium task categories',
      },
    )
    return result || []
  }

  /**
   * Get active staff users from Buildium
   * Used to populate searchable assignee dropdown in handoff form
   */
  async getStaffUsers(): Promise<BuildiumStaffUsersResponse> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<BuildiumStaffUsersResponse>
        >(API_ROUTES.INTEGRATIONS.BUILDIUM.STAFF_USERS)
        return response.data || []
      },
      {
        errorContext: 'Error fetching Buildium staff users',
      },
    )
    return result || []
  }

  /**
   * [ADMIN] Get active staff users from Buildium for a specific company
   */
  async getAdminStaffUsers(
    companyId: string,
  ): Promise<BuildiumStaffUsersResponse> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<BuildiumStaffUsersResponse>
        >(
          `${API_ROUTES.ADMIN.INTEGRATIONS.BUILDIUM.STAFF_USERS}?companyId=${companyId}`,
        )
        return response.data || []
      },
      {
        errorContext: 'Error fetching admin Buildium staff users',
      },
    )
    return result || []
  }

  /**
   * Get active vendors from Buildium
   * Used to populate searchable vendor dropdown in handoff form
   */
  async getVendors(): Promise<BuildiumVendorsResponse> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<BuildiumVendorsResponse>
        >(API_ROUTES.INTEGRATIONS.BUILDIUM.VENDORS)
        return response.data || []
      },
      {
        errorContext: 'Error fetching Buildium vendors',
      },
    )
    return result || []
  }

  /**
   * [ADMIN] Get active vendors from Buildium for a specific company
   */
  async getAdminVendors(companyId: string): Promise<BuildiumVendorsResponse> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<BuildiumVendorsResponse>
        >(
          `${API_ROUTES.ADMIN.INTEGRATIONS.BUILDIUM.VENDORS}?companyId=${companyId}`,
        )
        return response.data || []
      },
      {
        errorContext: 'Error fetching admin Buildium vendors',
      },
    )
    return result || []
  }

  /**
   * Disconnect Buildium integration
   * Uses the generic API key credentials disconnect endpoint
   */
  async disconnect(): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.INTEGRATIONS.API_KEY_CREDENTIALS.DELETE,
      { integrationType: ApiKeyCredentialIntegrationType.BUILDIUM },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'DELETE',
        })
      },
      {
        errorContext: 'Error disconnecting Buildium integration',
      },
    )
  }

  /**
   * [ADMIN] Disconnect Buildium integration for a specific company
   * Uses the generic admin API key credentials disconnect endpoint
   */
  async disconnectAdmin(companyId: string): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.ADMIN.INTEGRATIONS.API_KEY_CREDENTIALS.DELETE,
      { integrationType: ApiKeyCredentialIntegrationType.BUILDIUM },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'DELETE',
          },
        )
      },
      {
        errorContext: 'Error disconnecting admin Buildium integration',
      },
    )
  }
}

// Export singleton instance
export const buildiumClient = new BuildiumClient()
